/**
 * External store for Default email uiLocale auto-sync status.
 * Hook writes; Account settings (EmailLanguageBlock) reads + Retry.
 */

export type EmailLocaleSyncStatus = 'idle' | 'syncing' | 'error';

type Listener = () => void;

let status: EmailLocaleSyncStatus = 'idle';
let lastError: unknown = null;
const listeners = new Set<Listener>();

/** Registered by useSyncEmailLocaleOnLanguageChange while mounted. */
let retryHandler: (() => void) | null = null;

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

export function subscribeEmailLocaleSyncStatus(listener: Listener): () => void {
  listeners.add(listener);
  return (): void => {
    listeners.delete(listener);
  };
}

export function getEmailLocaleSyncStatusSnapshot(): EmailLocaleSyncStatus {
  return status;
}

export function getLastEmailLocaleSyncError(): unknown {
  return lastError;
}

export function setEmailLocaleSyncStatus(
  next: EmailLocaleSyncStatus,
  error: unknown = null,
): void {
  const nextError = next === 'error' ? error : null;
  if (status === next && lastError === nextError) {
    return;
  }
  status = next;
  lastError = nextError;
  emit();
}

/**
 * Hook registers a live-uiLocale re-enqueue callback.
 * Pass null on unmount.
 */
export function registerEmailLocaleSyncRetry(handler: (() => void) | null): void {
  retryHandler = handler;
}

/** Clear error and re-enqueue live UI locale sync (via registered hook handler). */
export function requestEmailLocaleSyncRetry(): void {
  lastError = null;
  if (status !== 'idle') {
    status = 'idle';
    emit();
  }
  retryHandler?.();
}

/** Test / HMR helper. */
export function resetEmailLocaleSyncStatusStore(): void {
  status = 'idle';
  lastError = null;
  retryHandler = null;
  emit();
}
